import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Building2,
  Receipt,
  Globe,
  Mail,
  Phone,
  MapPin,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const SettingsOrganization = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [orgSettings, setOrgSettings] = useState({
    companyName: '',
    contactEmail: '',
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    timezone: 'America/New_York',
    invoicePrefix: 'INV',
    invoiceNotes: '',
    taxRate: 0
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin';

  const handleSave = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      toast.success('Organization settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperAdmin && !isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Only admins can access organization settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="settings-organization-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Organization Settings
        </h1>
        <p className="text-slate-500 mt-1">
          {isSuperAdmin 
            ? 'Configure default organization settings for all tenants' 
            : "Manage your organization's information"
          }
        </p>
      </div>

      {/* Company Information */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Building2 size={20} className="text-slate-500" />
            Company Information
          </CardTitle>
          <CardDescription>Basic company details for invoices and communications</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input 
                value={orgSettings.companyName}
                onChange={(e) => setOrgSettings({ ...orgSettings, companyName: e.target.value })}
                placeholder="Your Company Name"
                data-testid="company-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <div className="relative">
                <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  value={orgSettings.website}
                  onChange={(e) => setOrgSettings({ ...orgSettings, website: e.target.value })}
                  placeholder="https://www.company.com"
                  className="pl-10"
                  data-testid="website-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  type="email"
                  value={orgSettings.contactEmail}
                  onChange={(e) => setOrgSettings({ ...orgSettings, contactEmail: e.target.value })}
                  placeholder="contact@company.com"
                  className="pl-10"
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  value={orgSettings.phone}
                  onChange={(e) => setOrgSettings({ ...orgSettings, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="pl-10"
                  data-testid="phone-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <MapPin size={20} className="text-slate-500" />
            Address
          </CardTitle>
          <CardDescription>Company address for invoices</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label>Street Address</Label>
            <Input 
              value={orgSettings.address}
              onChange={(e) => setOrgSettings({ ...orgSettings, address: e.target.value })}
              placeholder="123 Business Street, Suite 100"
              data-testid="address-input"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input 
                value={orgSettings.city}
                onChange={(e) => setOrgSettings({ ...orgSettings, city: e.target.value })}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input 
                value={orgSettings.state}
                onChange={(e) => setOrgSettings({ ...orgSettings, state: e.target.value })}
                placeholder="State"
              />
            </div>
            <div className="space-y-2">
              <Label>ZIP Code</Label>
              <Input 
                value={orgSettings.zipCode}
                onChange={(e) => setOrgSettings({ ...orgSettings, zipCode: e.target.value })}
                placeholder="12345"
              />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input 
                value={orgSettings.country}
                onChange={(e) => setOrgSettings({ ...orgSettings, country: e.target.value })}
                placeholder="Country"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoice Settings */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Receipt size={20} className="text-slate-500" />
            Invoice Settings
          </CardTitle>
          <CardDescription>Configure invoice generation preferences</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Invoice Number Prefix</Label>
              <Input 
                value={orgSettings.invoicePrefix}
                onChange={(e) => setOrgSettings({ ...orgSettings, invoicePrefix: e.target.value.toUpperCase() })}
                placeholder="INV"
                maxLength={5}
                data-testid="invoice-prefix-input"
              />
              <p className="text-xs text-slate-500">Preview: {orgSettings.invoicePrefix || 'INV'}-00001</p>
            </div>
            <div className="space-y-2">
              <Label>Default Tax Rate (%)</Label>
              <Input 
                type="number"
                value={orgSettings.taxRate}
                onChange={(e) => setOrgSettings({ ...orgSettings, taxRate: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                min={0}
                max={100}
                step={0.01}
                data-testid="tax-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Input 
                value={orgSettings.timezone}
                onChange={(e) => setOrgSettings({ ...orgSettings, timezone: e.target.value })}
                placeholder="America/New_York"
                data-testid="timezone-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Default Invoice Notes</Label>
            <Textarea 
              value={orgSettings.invoiceNotes}
              onChange={(e) => setOrgSettings({ ...orgSettings, invoiceNotes: e.target.value })}
              placeholder="Payment terms, thank you message, etc."
              rows={3}
              data-testid="invoice-notes-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave}
          disabled={loading}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="save-org-settings-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Organization Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsOrganization;
